<template>
	<div
		class="event-leaderboard"
		:class="{ 'failed-loading': !usersLoaded }"
		:data-event-type="attributes.type"
	>
		<div class="event-leaderboard__title">
			{{ translate("event.event_ranking") }}
		</div>
		<HorizontalDecorDivider :is-golden="true" />
		<template v-if="usersLoaded === true">
			<div class="event-leaderboard__buttons">
				<AppTextButton
					class="event-leaderboard__btn"
					:type="
						isGlobal
							? StatusTypes.DEFAULT_ACTIVE
							: StatusTypes.DEFAULT
					"
					:text="translate('ui.world')"
					@click="isGlobal = true"
				/>
				<AppTextButton
					class="event-leaderboard__btn"
					:type="
						!isGlobal
							? StatusTypes.DEFAULT_ACTIVE
							: StatusTypes.DEFAULT
					"
					:text="translate('ui.country')"
					@click="isGlobal = false"
				/>
			</div>
			<div class="event-leaderboard__item-header">
				{{ translate("ui.top_players") }}
			</div>
			<div class="event-leaderboard__container">
				<LeaderboardRow
					class="event-leaderboard__item-subheader"
					rank="#"
					country=""
					:name="translate('ui.player')"
					:score="productionText"
					bold
					:icon="ResourceType.EVENTIUM"
				/>
				<HorizontalDecorDivider
					:withSides="false"
					:is-golden="isCollectableEvent(attributes.type)"
					:is-red="
						!!attributes.type &&
						[
							EventCategory.CHRISTMAS,
							EventCategory.FIREFEST,
							EventCategory.PIRATES,
						].includes(attributes.type)
					"
				/>
				<div
					:class="`event-leaderboard__item--${getSkin(item, i)}`"
					class="event-leaderboard__item"
					v-for="(item, i) in users1"
					:key="i"
				>
					<LeaderboardRow
						:rank="item.placement > 0 ? item.placement : ''"
						:country="item.country"
						:name="resolveName(item)"
						:score="item.score"
						:icon="ResourceType.EVENTIUM"
						:skin="getSkin(item, i)"
					/>
					<HorizontalDecorDivider
						:with-sides="false"
						:is-golden="isCollectableEvent(attributes.type)"
						:is-red="
							!!attributes.type &&
							[
								EventCategory.CHRISTMAS,
								EventCategory.FIREFEST,
								EventCategory.PIRATES,
							].includes(attributes.type)
						"
					/>
				</div>
				<HorizontalDecorDivider
					v-if="users2.length > 0"
					class="event-leaderboard__divider-mid"
				/>
				<div
					class="event-leaderboard__item"
					:class="`event-leaderboard__item--${getSkin(item, i)}`"
					v-for="(item, i) in users2"
					:key="i"
				>
					<LeaderboardRow
						:rank="item.placement > 0 ? item.placement : ''"
						:country="item.country"
						:name="resolveName(item)"
						:score="item.score"
						:icon="ResourceType.EVENTIUM"
						:skin="getSkin(item, i)"
					/>
					<HorizontalDecorDivider
						:with-sides="false"
						:is-golden="isCollectableEvent(attributes.type)"
						:is-red="
							!!attributes.type &&
							[
								EventCategory.CHRISTMAS,
								EventCategory.FIREFEST,
								EventCategory.PIRATES,
							].includes(attributes.type)
						"
					/>
				</div>
			</div>
			<!-- TODO: Sticky footer bez fade -->
			<HorizontalDecorDivider :is-golden="true" />

			<div class="event-leaderboard__footer">
				<div class="icon-info-circle" />
				<span class="leaderboard__info">
					{{
						translate("ui.ranking_update_minutes").replace(
							"{number}",
							"30",
						)
					}}
				</span>
			</div>
		</template>
		<template v-else-if="usersLoaded === false">
			<div class="failed-loading__container">
				<div class="failed-loading__header">
					{{ translate("ui.username") }}
				</div>
				<div class="failed-loading__perex">
					{{ translate("ui.ranking_username_required") }}
				</div>
				<AppTextButton
					class="failed-loading__button"
					:type="
						isCollectableEvent(attributes.type) ||
						(attributes.type &&
							[
								EventCategory.FIREFEST,
								EventCategory.SOLAR,
								EventCategory.HUNTING,
							].includes(attributes.type))
							? StatusTypes.WARNING
							: StatusTypes.PRIMARY
					"
					:text="translate('ui.enter_name')"
					@click="showChangeNamePopup"
				/>
			</div>
		</template>
		<template v-else>
			<div class="failed-loading__container">
				<div class="failed-loading__header">
					{{ translate("ui.loading.non_translatable") }}
				</div>
			</div>
		</template>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import LeaderboardRow from "@/components/Leaderboard/LeaderboardRow.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import { isCollectableEvent, useEventStore } from "@/event";
import { ResourceType } from "@/game/entities/config/resources";
import useSocketService from "@/hooks/useSocketService";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import useUserStore from "@/store/useUserStore";
import type {
	EventLeaderboardResult,
	EventOverviewResult,
	Leaderboard,
} from "@/types";
import { EventCategory, SkinTypes, StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, onMounted, ref } from "vue";

const socketService = markRaw(useSocketService());
const userStore = useUserStore();
const eventStore = useEventStore();
const { baseEventiumIncome, attributes } = storeToRefs(eventStore);

const resourcesStore = useResourcesStore();
const { eventiumAccumulated } = storeToRefs(resourcesStore);

const { nameChanged, userName, userId } = storeToRefs(userStore);

const isGlobal = ref<boolean>(true);

const usersLoaded = ref<boolean | null>(null);

const productionText = computed((): string => {
	if (isCollectableEvent(attributes.value.type)) {
		return translate("event.total_produced");
	}
	return translate("ui.production");
});

const scoreValue = computed((): number => {
	if (isCollectableEvent(attributes.value.type)) {
		return eventiumAccumulated.value;
	}
	return baseEventiumIncome.value;
});

const users1 = computed((): Leaderboard[] => {
	return isGlobal.value ? [...users1Global.value] : users1Country.value;
});
const users2 = computed((): Leaderboard[] => {
	return isGlobal.value ? users2Global.value : users2Country.value;
});

const users1Country = ref<Leaderboard[]>([]);
const users2Country = ref<Leaderboard[]>([]);

const users1Global = ref<Leaderboard[]>([]);
const users2Global = ref<Leaderboard[]>([]);

const loadUsers = (): void => {
	const cachedData = eventStore.leaderboards;
	if (
		cachedData !== null &&
		eventStore.leaderboardLoadTimestamp &&
		eventStore.leaderboardLoadTimestamp > Date.now() - 1000 * 60 * 5
	) {
		setUsersData(cachedData);
		return;
	}
	socketService.getEventOverview().then((data: EventOverviewResult): void => {
		if (data?.leaderboards) {
			eventStore.setLeaderboardData(data.leaderboards);
			setUsersData(data.leaderboards as EventLeaderboardResult);
		}
	});
};

const setUsersData = (data: EventLeaderboardResult): void => {
	const users2ServedGlobal = data.globalAroundPlayer.length > 0;
	const users2ServedCountry = data.countryAroundPlayer.length > 0;
	users2Global.value = [];
	users2Country.value = [];
	users1Global.value = sortUsers(data.globalTop20, users2ServedGlobal);
	users1Country.value = sortUsers(data.countryTop20, users2ServedCountry);
	const playerPlacementGlobal = data.globalAroundPlayer.find(
		(user: Leaderboard): boolean => user.user_id === userId.value,
	)?.placement;
	const playerPlacementCountry = data.countryAroundPlayer.find(
		(user: Leaderboard): boolean => user.user_id === userId.value,
	)?.placement;
	if (playerPlacementGlobal && playerPlacementGlobal > 30) {
		users2Global.value = sortUsers(
			data.globalAroundPlayer,
			users2ServedGlobal,
		);
	}
	if (playerPlacementCountry && playerPlacementCountry > 30) {
		users2Country.value = sortUsers(
			data.countryAroundPlayer,
			users2ServedCountry,
		);
	}

	usersLoaded.value = true;
};

const resolveName = (user: Leaderboard): string => {
	return user.user_id === userId.value ? userName.value : user.user_name;
};

const sortUsers = (
	users: Leaderboard[],
	users2Served: boolean,
): Leaderboard[] => {
	const bestPlacement = users[0]?.placement || 1;
	let usersCopy = users.map(
		(user: Leaderboard): Leaderboard => ({
			...user,
		}),
	);

	const player = usersCopy.find((user: Leaderboard): boolean => {
		return user.user_id === userId.value;
	});

	if (player) {
		player.user_name = userName.value;
		player.score = scoreValue.value;
	} else if (!users2Served) {
		usersCopy.push({
			placement: -1,
			user_name: userName.value,
			user_id: userId.value,
			country: userStore.country,
			score: scoreValue.value,
			country_placement: 1,
		});
	}

	usersCopy.sort((a: Leaderboard, b: Leaderboard): number => {
		return b.score - a.score;
	});

	let rankCorrection = 0;
	usersCopy = usersCopy.map((user: Leaderboard, i: number): Leaderboard => {
		if (!users2Served && user.user_id === userId.value) {
			rankCorrection = 1;
			return user;
		}
		user.placement = bestPlacement + i - rankCorrection;
		return user;
	});

	return usersCopy;
};

const getSkin = (user: Leaderboard, i: number): SkinTypes | null => {
	if (user.user_id === userId.value) return SkinTypes.LIGHT;
	if (i % 2 === 0) return SkinTypes.DARK;
	return null;
};

const showChangeNamePopup = (): void => {
	eventStore.setEventPopupActive(false);

	usePopupStore().showPopup({
		id: PopupDictionary.CHANGE_NAME,
		type: PopupType.CHANGE_NAME,
		dropOnInterrupt: true,
		heading: translate("ui.username"),
		height: 13.75,
		isPaddingless: true,
		closeCallback: async (): Promise<void> => {
			eventStore.setEventPopupActive(true);

			if (!nameChanged.value) return;
			loadUsers();
		},
	});
};

onMounted((): void => {
	if (nameChanged.value) {
		loadUsers();
		return;
	} else {
		usersLoaded.value = false;
	}
});
</script>

<style lang="scss" scoped>
$baseTexture: url($path-images + "backgrounds/textures/window-texture-bg.avif")
	repeat center;

.event-leaderboard {
	width: 100%;
	@include flex-col-start;
	position: relative;
	gap: 0.5rem;
	padding: 1.25rem;
	padding-top: 0;

	&__buttons {
		@include flex-row-center;
		gap: 1.25rem;

		.event-leaderboard__btn {
			width: 7.875rem;
		}
	}

	&__container {
		display: flex;
		flex-direction: column;
		width: 100%;
	}
	&__title {
		padding-top: 0.5rem;
		width: 100%;
		color: #f4d278;
		font-weight: bold;
		font-size: 1.25rem;
		line-height: 1;
		text-align: center;
		text-transform: uppercase;
	}
	&__item {
		width: 100%;
		&-subheader {
			width: 100%;
			@include flex-row-between;
			* {
				font-weight: 800;
			}
			.event-leaderboard-row__name {
				flex: 1;
			}
		}
		&-header {
			color: #f4d278;
			font-weight: bold;
			font-size: 1rem;
			text-transform: uppercase;
		}
		&-title {
			width: 80%;
		}
		&-text {
			color: #fff3dd;
		}
	}
	&__footer {
		@include flex-row-center;
		gap: 0.25rem;
		width: 100%;
		height: 2rem;
		color: #fff3dd;
	}

	:deep(.horizontal-decor-divider) {
		width: 100%;
	}

	&__divider-mid {
		margin: 0.5rem 0;

		:deep(.cls-1) {
			fill: #f4d278;
		}
	}

	&.failed-loading {
		justify-content: center;
		height: 45vh;
	}
	.failed-loading {
		&__container {
			display: flex;
			flex: 1;
			flex-direction: column;
			align-items: center;
			gap: 0.5rem;
			margin-top: 1rem;
			color: #fff3dd;
		}
		&__header {
			font-weight: 800;
			font-size: 1.125rem;
		}
		&__perex {
			margin-bottom: 1rem;
			font-size: 1rem;
			text-align: center;
		}
	}
	&[data-event-type="christmas"] {
		.event-leaderboard__item--dark {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: rgba(31, 6, 6, 0.8);
			}
		}
		.event-leaderboard__item--light {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: #c8a05d;
			}
			:deep(.leaderboard-row--light) {
				color: #661094;
			}
		}
	}
	&[data-event-type="easter"] {
		.event-leaderboard__item--dark {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: rgba(30, 31, 28, 0.5);
			}
		}
		.event-leaderboard__item--light {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: #e2c798;
			}
			:deep(.leaderboard-row--light) {
				color: #661094;
			}
		}
		:deep(.cls-1) {
			fill: #928b18;
		}
	}
	&[data-event-type="firefest"] {
		.event-leaderboard__item--dark {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: rgba(30, 31, 28, 0.5);
			}
		}
		.event-leaderboard__item--light {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: #e2c798;
			}
			:deep(.leaderboard-row--light) {
				color: #661094;
			}
		}
		:deep(.cls-1) {
			fill: #928b18;
		}
	}
	&[data-event-type="solarfair"] {
		.event-leaderboard__item--dark {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: rgba(30, 31, 28, 0.5);
			}
		}
		.event-leaderboard__item--light {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: #e2c798;
			}
			:deep(.leaderboard-row--light) {
				color: #661094;
			}
		}
		:deep(.cls-1) {
			fill: #f4d278;
		}
	}
	&[data-event-type="fishing"] {
		.event-leaderboard__item--dark {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: #1c1d1f;
			}
		}
		.event-leaderboard__item--light {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: #e2c798;
			}
			:deep(.leaderboard-row--light) {
				color: #661094;
			}
		}
		:deep(.horizontal-decor-divider--without-sides) {
			z-index: 1;
		}
		:deep(.cls-1) {
			fill: #156e8f;
		}
	}
	&[data-event-type="hunting"] {
		.event-leaderboard__item--dark {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: rgba(30, 31, 28, 0.5);
			}
		}
		.event-leaderboard__item--light {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: #e2c798;
			}
			:deep(.leaderboard-row--light) {
				color: #661094;
			}
		}
		:deep(.horizontal-decor-divider--without-sides) {
			z-index: 1;
		}
		:deep(.cls-1) {
			fill: #256d28;
		}
	}
	&[data-event-type="halloween"] {
		.event-leaderboard__item--dark {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: rgba(30, 31, 28, 0.5);
			}
		}
		.event-leaderboard__item--light {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: #e2c798;
			}
			:deep(.leaderboard-row--light) {
				color: #661094;
			}
		}
		:deep(.cls-1) {
			fill: #815ed3;
		}
	}
	&[data-event-type="pirates"] {
		.event-leaderboard__item--dark {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: rgba(28, 29, 31, 0.5);
			}
		}
		.event-leaderboard__item--light {
			:deep(.horizontal-decor-sides-cracked-bg__path) {
				fill: #e2c798;
			}
			:deep(.leaderboard-row--light) {
				color: #661094;
			}
		}
		:deep(.horizontal-decor-divider--without-sides .cls-2) {
			fill: #c31525;
		}
	}
}
</style>
